import './CreateDialogSortingMap.scss';

import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useCallback, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import AutoCompleteInput from '../../../../../Forms/AutoCompleteInput/AutoCompleteInput';

type PortRowProps = {
  index: number;
  pointTypes: { value: number; label: string }[];
  removePortsFields: (index: number) => void;
  addPointToPort: (port: number) => void;
};

export default function PortRow({
  index,
  pointTypes,
  removePortsFields,
  addPointToPort,
}: PortRowProps): JSX.Element {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const [searchTerm, setSearchTerm] = useState('');
  const ports = useWatch({ name: 'ports', control }) || [];
  const row = ports[index] || {};

  const url =
    `/logistics-point?pointTypeId=${row.pointTypeId || 1}` +
    `&keyword=${encodeURIComponent(searchTerm)}`;
  const { data: optData, isLoading } = useAxiosHook<any>(url);

  // A point maps to exactly one port, so the api rejects a repeat - drop the points the other rows
  // already claimed instead of failing on save.
  const takenPointIds = useMemo(() => {
    return new Set(
      ports
        .filter((_: any, other: number) => other !== index)
        .map((p: any) => p.logisticsPointId)
        .filter(Boolean)
    );
  }, [ports, index]);

  const options = useMemo(() => {
    return (optData?.data || [])
      .filter((p: any) => !takenPointIds.has(p.id))
      .map((p: any) => ({
        value: p.id,
        label: p.name,
      }));
  }, [optData, takenPointIds]);

  const handleFilterChange = useCallback((val) => {
    setSearchTerm(val);
  }, []);

  const handleSelect = useCallback(
    (val) => {
      setValue(`ports.${index}.logisticsPointId`, val);
      const sel = options.find((o: any) => o.value === val);
      setValue(`ports.${index}.pointName`, sel?.label || '');
    },
    [index, options, setValue]
  );

  return (
    <tr key={row.id}>
      <td className="port-input">
        <Controller
          name={`ports.${index}.port`}
          render={({ field }) => (
            <InputText
              id={`ports.${index}.port`}
              name={`ports.${index}.port`}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </td>
      <td>
        <Controller
          control={control}
          name={`ports.${index}.pointTypeId`}
          render={({ field }) => (
            <Dropdown
              {...field}
              options={pointTypes}
              onChange={(e) => field.onChange(e.value)}
            />
          )}
        />
      </td>

      <td>
        <Controller
          control={control}
          name={`ports.${index}.logisticsPointId`}
          render={({ field }) => (
            <AutoCompleteInput
              filterValue={searchTerm !== '' ? searchTerm : row?.pointName}
              onFilterChange={handleFilterChange}
              options={options}
              isLoading={isLoading}
              placeholder={t('Search points')}
              value={field.value}
              onSelectionChange={handleSelect}
            />
          )}
        />
      </td>

      <td>
        <Button
          className="p-button-outlined"
          style={{ width: '2rem', height: '1.5rem', border: 'none' }}
          icon="fas fa-trash-alt"
          onClick={() => removePortsFields(index)}
        />
        <Button
          className="p-button-outlined"
          style={{ width: '2rem', height: '1.5rem', border: 'none' }}
          icon="fas fa-user-plus"
          type="button"
          tooltip={t('Add point to this port')}
          disabled={!row.port}
          onClick={() => addPointToPort(Number(row.port))}
        />
      </td>
    </tr>
  );
}
